/*
 * lotto/engine.js — the rules of the three games, with no DOM in sight.
 *
 * Three games share one engine:
 *   ru90  Русское лото: 90 barrels; a card is 3 rows × 9 columns with 15
 *         numbers, exactly 5 in every row; column 1 holds 1–9, column k holds
 *         the k-th ten (10–19 … 70–79) and the last column 80–90. Numbers in a
 *         column read top to bottom in ascending order, as on a printed card.
 *   uk90  90-ball bingo: the same 3×9 ticket, sold in STRIPS of six tickets
 *         that between them hold every number 1–90 exactly once, and every
 *         ticket has at least one number in every column.
 *   us75  75-ball bingo: 5×5, B 1–15 · I 16–30 · N 31–45 · G 46–60 · O 61–75,
 *         the centre square is FREE.
 *
 * WHY CARDS ARE A FUNCTION OF (game, code, number)
 * ────────────────────────────────────────────────
 * The host never stores a card. A card is regenerated from a short game code
 * and its printed number by a seeded generator, so «card 17 of set K7PQ2» is
 * the same card on the printout, on a player's phone opened from a link, and
 * in the host's «check this card» box — nobody has to read numbers aloud to
 * verify a win, and nothing has to be sent anywhere to agree on a card.
 * Changing a single rule below changes every card ever printed, which is why
 * tests/js/lotto-engine.test.mjs pins sample cards byte for byte.
 *
 * The DRAW is the opposite: it must not be predictable, so it uses
 * crypto.getRandomValues with rejection sampling (no modulo bias) and never
 * the seeded generator.
 */

export const GAMES = {
    ru90: { id: 'ru90', max: 90, rows: 3, cols: 9, goals: ['line1', 'line2', 'full'] },
    uk90: { id: 'uk90', max: 90, rows: 3, cols: 9, goals: ['line1', 'line2', 'full'], strip: 6 },
    us75: { id: 'us75', max: 75, rows: 5, cols: 5, goals: ['line', 'corners', 'x', 'full'] },
};
export const GAME_IDS = Object.keys(GAMES);

/* ── game code ───────────────────────────────────────────────────────────────
 * No vowels (no accidental words on a card), no 0/O, 1/I/L confusion. Five
 * characters = 28^5 ≈ 17 million sets. */
export const CODE_ALPHABET = '23456789BCDFGHJKMNPQRSTVWXYZ';
export const CODE_LEN = 5;

/* A host typing the code from a printed card on a Russian keyboard layout
 * gets Cyrillic look-alikes: «К7РQ2». Map the ones that look identical. */
const CYR = { 'А': 'A', 'В': 'B', 'С': 'C', 'Е': 'E', 'Н': 'H', 'К': 'K', 'М': 'M', 'О': 'O', 'Р': 'P', 'Т': 'T', 'Х': 'X', 'У': 'Y' };

export function normalizeCode(raw) {
    let s = String(raw == null ? '' : raw).toUpperCase().replace(/[\s\-–—_.·]/g, '');
    s = s.replace(/[АВСЕНКМОРТХУ]/g, (ch) => CYR[ch] || ch);
    if (s.length !== CODE_LEN) return null;
    for (const ch of s) if (CODE_ALPHABET.indexOf(ch) < 0) return null;
    return s;
}

export function newCode(randomU32 = cryptoU32) {
    let s = '';
    for (let i = 0; i < CODE_LEN; i++) s += CODE_ALPHABET[pickIndex(CODE_ALPHABET.length, randomU32)];
    return s;
}

/* ── randomness ──────────────────────────────────────────────────────────── */

export function cryptoU32() {
    const a = new Uint32Array(1);
    globalThis.crypto.getRandomValues(a);
    return a[0];
}

/* Uniform index in [0, n) without modulo bias. */
export function pickIndex(n, randomU32 = cryptoU32) {
    if (!(n > 0)) throw new Error('pickIndex: empty range');
    const limit = Math.floor(0x100000000 / n) * n;
    for (let guard = 0; guard < 64; guard++) {
        const x = randomU32() >>> 0;
        if (x < limit) return x % n;
    }
    return (randomU32() >>> 0) % n;       // statistically unreachable
}

/* xmur3 string hash → sfc32 generator. Both are public-domain, tiny, and
 * give the same stream on every JS engine (pure 32-bit integer maths). */
function xmur3(str) {
    let h = 1779033703 ^ str.length;
    for (let i = 0; i < str.length; i++) {
        h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    return function () {
        h = Math.imul(h ^ (h >>> 16), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        return (h ^= h >>> 16) >>> 0;
    };
}
export function seeded(seedStr) {
    const next = xmur3(String(seedStr));
    let a = next(), b = next(), c = next(), d = next();
    const u32 = function () {
        a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0;
        let t = (a + b) | 0;
        a = b ^ (b >>> 9);
        b = (c + (c << 3)) | 0;
        c = (c << 21) | (c >>> 11);
        d = (d + 1) | 0;
        t = (t + d) | 0;
        c = (c + t) | 0;
        return t >>> 0;
    };
    for (let i = 0; i < 12; i++) u32();       // warm-up: decorrelate close seeds
    return u32;
}

function shuffle(arr, u32) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = pickIndex(i + 1, u32);
        const t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
}

/* ── 3 × 9 tickets (ru90, uk90) ───────────────────────────────────────────── */

/* Column k of a 90-number ticket: 1–9, 10–19 … 70–79, 80–90. */
export function columnNumbers(k) {
    const lo = k === 0 ? 1 : k * 10;
    const hi = k === 8 ? 90 : k * 10 + 9;
    const out = [];
    for (let n = lo; n <= hi; n++) out.push(n);
    return out;
}
export function columnOf90(n) { return n >= 90 ? 8 : Math.floor(n / 10); }

/* Which rows get a number in each column, given how many numbers each column
 * holds (1–3, sum 15). Rows take 5 each. Columns are placed largest first,
 * each into the rows with the most room left (ties broken at random) — the
 * constructive proof of the Gale–Ryser theorem, so it cannot get stuck for
 * these sums (2a+b = 6 for a three-number and b two-number columns). */
function layoutRows(counts, u32) {
    const room = [5, 5, 5];
    const mask = counts.map(() => [false, false, false]);
    const order = shuffle(counts.map((_, i) => i), u32).sort((x, y) => counts[y] - counts[x]);
    for (const col of order) {
        const rows = shuffle([0, 1, 2], u32).sort((x, y) => room[y] - room[x]);
        for (let k = 0; k < counts[col]; k++) {
            const r = rows[k];
            if (room[r] <= 0) return null;
            room[r]--;
            mask[col][r] = true;
        }
    }
    return room.every((x) => x === 0) ? mask : null;
}

/* Fill a 3×9 grid: grid[row][col] = number or 0 for a blank cell. */
function placeTicket(counts, colNums, u32) {
    let mask = null;
    for (let tries = 0; tries < 50 && !mask; tries++) mask = layoutRows(counts, u32);
    if (!mask) throw new Error('ticket layout failed');
    const grid = [[], [], []];
    for (let c = 0; c < 9; c++) {
        const nums = colNums[c].slice().sort((a, b) => a - b);
        let i = 0;
        for (let r = 0; r < 3; r++) grid[r][c] = mask[c][r] ? nums[i++] : 0;
    }
    return grid;
}

/* One stand-alone Russian lotto card. Every column gets at least one number
 * (as on the classic cards and required for 90-ball tickets), at most three;
 * six extra numbers are spread over the columns at random. */
export function ruCard(code, n) {
    const u32 = seeded('ru90|' + code + '|' + n);
    const counts = new Array(9).fill(1);
    let extra = 6;
    while (extra > 0) {
        const c = pickIndex(9, u32);
        if (counts[c] < 3) { counts[c]++; extra--; }
    }
    const colNums = counts.map((k, c) => shuffle(columnNumbers(c), u32).slice(0, k));
    return makeTicket('ru90', code, n, placeTicket(counts, colNums, u32));
}

/* A strip of six 90-ball tickets holding 1–90 exactly once.
 *
 * Every ticket has one number per column to begin with (6 × 9 = 54); the other
 * 36 are «extras» e[t][c] ∈ {0,1,2} with row sums 6 (15 − 9) and column sums
 * 3, 4 … 4, 5 (column sizes 9, 10 … 10, 11 minus 6). Handed out one unit at a
 * time to the ticket with the most still to take, largest column first —
 * again a Gale–Ryser construction, retried with fresh randomness in the
 * (unobserved) event it paints itself into a corner. */
export function ukStrip(code, s) {
    const u32 = seeded('uk90|' + code + '|strip|' + s);
    let extras = null;
    for (let tries = 0; tries < 100 && !extras; tries++) extras = stripExtras(u32);
    if (!extras) throw new Error('strip layout failed');
    const counts = extras.map((row) => row.map((e) => e + 1));
    // Deal each column's numbers to the six tickets.
    const dealt = Array.from({ length: 6 }, () => new Array(9));
    for (let c = 0; c < 9; c++) {
        const pool = shuffle(columnNumbers(c), u32);
        let at = 0;
        for (let t = 0; t < 6; t++) { dealt[t][c] = pool.slice(at, at + counts[t][c]); at += counts[t][c]; }
    }
    const tickets = [];
    for (let t = 0; t < 6; t++) {
        tickets.push(makeTicket('uk90', code, (s - 1) * 6 + t + 1, placeTicket(counts[t], dealt[t], u32)));
    }
    return tickets;
}

function stripExtras(u32) {
    const colNeed = [3, 4, 4, 4, 4, 4, 4, 4, 5];
    const rowNeed = [6, 6, 6, 6, 6, 6];
    const e = Array.from({ length: 6 }, () => new Array(9).fill(0));
    const cols = shuffle([0, 1, 2, 3, 4, 5, 6, 7, 8], u32).sort((a, b) => colNeed[b] - colNeed[a]);
    for (const c of cols) {
        for (let k = colNeed[c]; k > 0; k--) {
            const cand = shuffle([0, 1, 2, 3, 4, 5], u32)
                .filter((t) => e[t][c] < 2 && rowNeed[t] > 0)
                .sort((a, b) => rowNeed[b] - rowNeed[a]);
            if (!cand.length) return null;
            const t = cand[0];
            e[t][c]++; rowNeed[t]--;
        }
    }
    return rowNeed.every((x) => x === 0) ? e : null;
}

export function ukTicket(code, n) {
    const s = Math.ceil(n / 6);
    return ukStrip(code, s)[(n - 1) % 6];
}

function makeTicket(game, code, n, grid) {
    const numbers = [];
    for (const row of grid) for (const x of row) if (x) numbers.push(x);
    numbers.sort((a, b) => a - b);
    return { game, code, n, grid, numbers };
}

/* ── 75-ball card ────────────────────────────────────────────────────────── */

export const BINGO = 'BINGO';
export function letterOf75(n) { return BINGO[Math.floor((n - 1) / 15)]; }

export function usCard(code, n) {
    const u32 = seeded('us75|' + code + '|' + n);
    const grid = [[], [], [], [], []];
    for (let c = 0; c < 5; c++) {
        const pool = [];
        for (let x = c * 15 + 1; x <= c * 15 + 15; x++) pool.push(x);
        const pick = shuffle(pool, u32).slice(0, 5);
        for (let r = 0; r < 5; r++) grid[r][c] = (c === 2 && r === 2) ? 0 : pick[r];
    }
    const numbers = [];
    for (const row of grid) for (const x of row) if (x) numbers.push(x);
    numbers.sort((a, b) => a - b);
    return { game: 'us75', code, n, grid, numbers };
}

/* THE door: any game, any card number. */
export function card(game, code, n) {
    if (game === 'ru90') return ruCard(code, n);
    if (game === 'uk90') return ukTicket(code, n);
    if (game === 'us75') return usCard(code, n);
    throw new Error('unknown game ' + game);
}

/* ── the draw ────────────────────────────────────────────────────────────── */

export function remaining(game, drawn) {
    const max = GAMES[game].max;
    const seen = new Set(drawn);
    const out = [];
    for (let n = 1; n <= max; n++) if (!seen.has(n)) out.push(n);
    return out;
}

/* Next barrel out of the bag, or null when the bag is empty. */
export function drawNext(game, drawn, randomU32 = cryptoU32) {
    const left = remaining(game, drawn);
    if (!left.length) return null;
    return left[pickIndex(left.length, randomU32)];
}

/* ── winning ─────────────────────────────────────────────────────────────────
 * Everything is answered from the ORDER of the draw, not just the set: for
 * each pattern we know the call number on which it completed, so the host can
 * see «this card had a line on call 23» and settle who shouted first. */

function positions(drawn) {
    const pos = new Map();
    drawn.forEach((x, i) => { if (!pos.has(x)) pos.set(x, i); });
    return pos;
}

/* The winning lines of a card as lists of numbers (FREE square left out). */
export function lines(c) {
    if (c.game === 'us75') {
        const L = [];
        const g = c.grid;
        for (let r = 0; r < 5; r++) L.push({ kind: 'row', i: r, nums: g[r].filter(Boolean) });
        for (let k = 0; k < 5; k++) L.push({ kind: 'col', i: k, nums: g.map((row) => row[k]).filter(Boolean) });
        L.push({ kind: 'diag', i: 0, nums: [0, 1, 2, 3, 4].map((i) => g[i][i]).filter(Boolean) });
        L.push({ kind: 'diag', i: 1, nums: [0, 1, 2, 3, 4].map((i) => g[i][4 - i]).filter(Boolean) });
        return L;
    }
    return c.grid.map((row, r) => ({ kind: 'row', i: r, nums: row.filter(Boolean) }));
}

/* Numbers that make up each 75-ball pattern other than «any line». */
function patternSets(c) {
    const g = c.grid;
    return {
        corners: [g[0][0], g[0][4], g[4][0], g[4][4]],
        x: [0, 1, 2, 3, 4].map((i) => g[i][i]).concat([0, 1, 2, 3, 4].map((i) => g[i][4 - i])).filter(Boolean),
        full: c.numbers.slice(),
    };
}

function doneAt(nums, pos) {
    let t = -1;
    for (const x of nums) {
        if (!pos.has(x)) return Infinity;
        const p = pos.get(x);
        if (p > t) t = p;
    }
    return t;
}
function missingOf(nums, pos) { return nums.filter((x) => !pos.has(x)); }

/*
 * evaluate(card, drawn, goal) →
 *   { met, at, number, missing, linesDone }
 *   met      the goal is complete with the barrels drawn so far
 *   at       1-based call number on which it completed (null if not met)
 *   number   the barrel that completed it
 *   missing  the fewest numbers still needed to complete the goal (sorted)
 *   linesDone  complete rows (tickets) / complete lines (75-ball)
 */
export function evaluate(c, drawn, goal) {
    const pos = positions(drawn);
    const L = lines(c);
    const times = L.map((l) => doneAt(l.nums, pos));
    const linesDone = times.filter((t) => t !== Infinity).length;
    let t = Infinity, missing = [];

    if (c.game === 'us75') {
        if (goal === 'line') {
            t = Math.min(...times);
            let best = null;
            for (const l of L) { const m = missingOf(l.nums, pos); if (!best || m.length < best.length) best = m; }
            missing = best;
        } else {
            const set = patternSets(c)[goal];
            if (!set) throw new Error('unknown goal ' + goal);
            t = doneAt(set, pos);
            missing = missingOf(Array.from(new Set(set)), pos);
        }
    } else {
        const need = goal === 'line1' ? 1 : goal === 'line2' ? 2 : goal === 'full' ? 3 : 0;
        if (!need) throw new Error('unknown goal ' + goal);
        const sorted = times.slice().sort((a, b) => a - b);
        t = sorted[need - 1];
        // The rows closest to done, as many as the goal needs.
        const rowsMissing = L.map((l) => missingOf(l.nums, pos)).sort((a, b) => a.length - b.length);
        missing = [].concat(...rowsMissing.slice(0, need));
    }
    missing.sort((a, b) => a - b);
    const met = t !== Infinity;
    return { met, at: met ? t + 1 : null, number: met ? drawn[t] : null, missing: met ? [] : missing, linesDone };
}

/* Near wins for a player's own marks (a Set of daubed numbers): which lines
 * are one number short. Used to light the row on the phone. */
export function oneToGo(c, marked) {
    const out = [];
    for (const l of lines(c)) {
        const left = l.nums.filter((x) => !marked.has(x));
        if (left.length === 1) out.push({ kind: l.kind, i: l.i, number: left[0] });
    }
    return out;
}

/* Scan a range of cards and list those that meet the goal, earliest first. */
export function winners(game, code, from, to, drawn, goal) {
    const out = [];
    for (let n = from; n <= to; n++) {
        const r = evaluate(card(game, code, n), drawn, goal);
        if (r.met) out.push({ n, at: r.at, number: r.number });
    }
    out.sort((a, b) => a.at - b.at || a.n - b.n);
    return out;
}

/* ── hash link for a player's phone ──────────────────────────────────────── */

export function cardHash(game, code, n) { return 'card=' + game + '.' + code + '.' + n; }
export function parseCardHash(hash) {
    const m = /(?:^|[#&])card=([a-z0-9]+)\.([A-Z0-9]+)\.(\d{1,4})(?:&|$)/.exec(String(hash || ''));
    if (!m || !GAMES[m[1]]) return null;
    const code = normalizeCode(m[2]);
    const n = parseInt(m[3], 10);
    if (!code || !(n >= 1 && n <= MAX_CARD)) return null;
    return { game: m[1], code, n };
}
export const MAX_CARD = 9999;
